import React, { useState, useEffect, useCallback } from 'react';
import { Card, List, Button, Space, Typography, Modal, Form, Input, Select, message, Tag, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, BarChartOutlined } from '@ant-design/icons';
import ReportService from '../services/reportBuilder.service';

const { Title } = Typography;
const { Option } = Select;

const ReportLibrary = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      // Load reports for all entities
      const entities = ['items', 'sales', 'customers', 'audit-logs'];
      const allReports = [];

      for (const entity of entities) {
        try {
          const response = await ReportService.getSavedSearches(entity, true, true);
          if (response && response.length > 0) {
            allReports.push(...response);
          }
        } catch (error) {
          // Continue with other entities if one fails
          console.warn(`Failed to load reports for ${entity}:`, error);
        }
      }

      // Remove duplicates based on ID
      const uniqueReports = allReports.filter((report, index, self) =>
        index === self.findIndex(r => r.id === report.id)
      );
      setReports(uniqueReports);
    } catch (error) {
      console.error('Error loading reports:', error);
      message.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = useCallback((report) => {
    // Open report in new window/tab
    const url = `/reports/viewer?id=${report.id}&entity=${report.entityType}`;
    window.open(url, '_blank');
  }, []);

  const handleEditReport = useCallback((report) => {
    // Open report builder with the report loaded
    const url = `/reports/builder?load=${report.id}&entity=${report.entityType}`;
    window.open(url, '_blank');
  }, []);

  const handleEditReportBuilder = useCallback((report) => {
    // Open report builder with the report loaded
    const url = `/reports/builder?load=${report.id}&entity=${report.entityType}`;
    window.open(url, '_blank');
  }, []);

  const handleEditReportModal = useCallback((report) => {
    setEditingReport(report);
    form.setFieldsValue({
      name: report.name,
      entityType: report.entityType,
      isPublic: report.isPublic
    });
    setModalVisible(true);
  }, [form]);

  const handleDeleteReport = useCallback(async (report) => {
    Modal.confirm({
      title: 'Delete Report',
      content: `Are you sure you want to delete "${report.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await ReportService.deleteSavedSearch(report.entityType, report.id);
          message.success('Report deleted successfully');
          loadReports();
        } catch (error) {
          console.error('Error deleting report:', error);
          message.error('Failed to delete report');
        }
      }
    });
  }, []);

  const handleSaveReport = useCallback(async (values) => {
    try {
      if (editingReport) {
        await ReportService.updateSavedSearch(editingReport.entityType, editingReport.id, {
          name: values.name,
          isPublic: values.isPublic
        });
        message.success('Report updated successfully');
      }
      setModalVisible(false);
      setEditingReport(null);
      form.resetFields();
      loadReports();
    } catch (error) {
      console.error('Error saving report:', error);
      message.error('Failed to save report');
    }
  }, [editingReport, form]);

  const handleCancel = useCallback(() => {
    setModalVisible(false);
    setEditingReport(null);
    form.resetFields();
  }, [form]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ padding: '20px' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: '20px' }}>
            <Col>
              <Title level={2}>Report Library</Title>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />}>
                Create New Report
              </Button>
            </Col>
          </Row>

          <Card>
        <List
          loading={loading}
          dataSource={reports}
          renderItem={(report) => (
            <List.Item
              actions={[
                <Button
                  key="view"
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewReport(report)}
                >
                  View
                </Button>,
                <Button
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => handleEditReportBuilder(report)}
                >
                  Edit
                </Button>,
                <Button
                  key="delete"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteReport(report)}
                >
                  Delete
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<BarChartOutlined style={{ fontSize: '24px', color: '#1890ff' }} />}
                title={
                  <Space>
                    {report.name}
                    {report.isPublic && <Tag color="green">Public</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    <span>Entity: <Tag>{report.entityType}</Tag></span>
                    <span>Created: {new Date(report.createdAt).toLocaleDateString()}</span>
                  </Space>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: 'No reports found. Create your first report!' }}
        />
      </Card>

      <Modal
        title={editingReport ? 'Edit Report' : 'Create Report'}
        visible={modalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveReport}
        >
          <Form.Item
            name="name"
            label="Report Name"
            rules={[{ required: true, message: 'Please enter report name' }]}
          >
            <Input placeholder="Enter report name" />
          </Form.Item>

          <Form.Item
            name="entityType"
            label="Entity Type"
            rules={[{ required: true, message: 'Please select entity type' }]}
          >
            <Select placeholder="Select entity type" disabled={!!editingReport}>
              <Option value="items">Items</Option>
              <Option value="sales">Sales</Option>
              <Option value="customers">Customers</Option>
              <Option value="audit-logs">Audit Logs</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="isPublic"
            label="Visibility"
            rules={[{ required: true, message: 'Please select visibility' }]}
          >
            <Select placeholder="Select visibility">
              <Option value={false}>Private</Option>
              <Option value={true}>Public</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingReport ? 'Update' : 'Create'} Report
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
        </div>
      </div>
    </div>
  );
};

export default ReportLibrary;